import { useRef } from "react"

import { PostAuthLoginBody as FormSchema } from "@boundless/bl-api-client/allauth/allauth.zod"
import {
  usePostAuthLogin,
  type PostAuthLoginMutationResult,
} from "@boundless/bl-api-client/allauth/auth"
import { ConflictResponseStatus } from "@boundless/bl-api-client/allauth/models"
import { usePublicBoundlessConfigRetrieveSuspense } from "@boundless/bl-api-client/public/services"
import { zodResolver } from "@hookform/resolvers/zod"
import mixpanel from "mixpanel-browser"
import { useForm, useWatch } from "react-hook-form"
import { z } from "zod"

import { FormConsentText } from "@/components/form-elements/FormConsentText"
import { FormInput } from "@/components/form-elements/FormInput"
import {
  FormTurnstile,
  type FormTurnstileHandle,
} from "@/components/form-elements/FormTurnstile"
import { Button, LoadingButton } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import { isAccountPendingEmailVerificationError } from "@/lib/account/utils"
import { getAuthClient, type ErrorType } from "@/lib/clients"
import { applyErrorsToForm } from "@/lib/forms/utils"
import { store } from "@/lib/store"
import { redirectTo } from "@/lib/utils"

import type {
  AuthenticationResponse,
  ConflictResponse,
  ErrorResponse,
} from "@boundless/bl-api-client/allauth/models"

type LoginErrorResponses =
  | ErrorResponse
  | ConflictResponse
  | AuthenticationResponse

/**
 * @summary Narrowing function to check if response is a Conflict Error (409)
 */
function isConflictError(
  error: ErrorType<LoginErrorResponses>,
): error is ErrorType<ConflictResponse> {
  return error.response?.status === ConflictResponseStatus.NUMBER_409
}

type LoginFormProps = React.ComponentPropsWithoutRef<"form"> & {
  onSuccess?: (response: PostAuthLoginMutationResult) => void
  onForgotPassword?: () => void
  // Called when login is refused because the email is not verified yet.
  // Verification is code-based, so the parent routes to the code screen to
  // finish it.
  onPendingVerification: (email: string) => void
  // Optional email to pre-populate (e.g. partner-collected value in the embed
  // flow). The field stays editable.
  email?: string | null
}

export function LoginForm({
  onSuccess,
  onForgotPassword,
  onPendingVerification,
  email,
  ...props
}: LoginFormProps) {
  const client = getAuthClient()

  const { data: boundlessConfig } = usePublicBoundlessConfigRetrieveSuspense()
  const turnstileRef = useRef<FormTurnstileHandle>(null)
  const turnstileEnabled =
    boundlessConfig.loginCaptchaEnabled &&
    Boolean(boundlessConfig.turnstileSiteKey)

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: email ?? "",
      password: "",
      captcha: "",
    },
  })
  const captchaToken = useWatch({ control: form.control, name: "captcha" })

  const { isPending, mutate: login } = usePostAuthLogin({
    mutation: {
      onSuccess: (response) => {
        if (response.data.user.redirect) {
          redirectTo(response.data.user.redirect)
          return
        }

        mixpanel.identify(response.data.user.id)

        store.setState({
          isAuthenticated: response.meta.is_authenticated,
          userRole: response.data.user.role,
        })

        onSuccess?.(response)
      },
      onError: (error) => {
        turnstileRef.current?.reset()
        if (isAccountPendingEmailVerificationError(error)) {
          onPendingVerification(form.getValues("email"))
          return
        }

        // A 409 error means that the user is already logged in. Unfortunately,
        // it doesn't returns the User data for `setUser` in the response, but
        // since it should *never* happen under normal conditions, we can force
        // a page reload and it should fetch the session data.
        if (isConflictError(error)) {
          window.location.reload()
        }

        applyErrorsToForm(form.setError, error)
      },
    },
  })

  function handleSubmit(data: z.infer<typeof FormSchema>) {
    login({ client, data })
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} {...props}>
        <div className="flex flex-col space-y-4">
          <FormInput
            name="email"
            placeholder="Email"
            type="email"
            autoComplete="email"
            className="w-full"
            // When the email is prefilled, the returning borrower's next action
            // is the password, so focus that field instead of the filled email.
            autoFocus={!email}
            required
            disabled={isPending}
          />
          <div>
            <FormInput
              name="password"
              placeholder="Password"
              type="password"
              autoComplete="current-password"
              className="mb-0 w-full pb-0"
              autoFocus={!!email}
              required
              disabled={isPending}
            />
            {onForgotPassword ? (
              <Button
                variant={"link"}
                className="p-0 text-neutral-600 hover:text-neutral-800"
                type="button"
                onClick={onForgotPassword}
              >
                Forgot password?
              </Button>
            ) : null}
          </div>

          {turnstileEnabled && (
            <FormTurnstile
              ref={turnstileRef}
              action="login"
              className="mx-auto"
              name="captcha"
              siteKey={boundlessConfig.turnstileSiteKey}
            />
          )}

          {form.formState.errors.root && (
            <p className="text-destructive text-center text-sm font-medium">
              {form.formState.errors.root.message}
            </p>
          )}

          <FormConsentText action="clicking 'Login' below" className="mt-8" />

          {isPending ? (
            <LoadingButton>Login</LoadingButton>
          ) : (
            <Button type="submit" disabled={turnstileEnabled && !captchaToken}>
              Login
            </Button>
          )}
        </div>
      </form>
    </Form>
  )
}

/**
 * The `verified` query string parameter indicates whether email verification
 * was successful or not. If it's not present, we don't show any message.
 * @param verified - The verification status of the email.
 */
export function VerifyResultText({
  verified,
}: {
  verified: boolean | undefined
}) {
  if (verified === true) {
    return (
      <h3 className="text-success my-4">
        Thank you for verifying your email address.
        <br />
        You may now log in.
      </h3>
    )
  } else if (verified === false) {
    return (
      <h3 className="text-destructive my-4">
        The verification link is invalid, has expired, or has already been used.
      </h3>
    )
  }
}

/**
 * The `resetComplete` query string parameter indicates whether password reset
 * was successful or not.
 * @param resetComplete - Whether the password reset was successful.
 */
export function PasswordResetResultText({
  resetComplete,
}: {
  resetComplete: boolean | undefined
}) {
  if (resetComplete === true) {
    return (
      <h3 className="text-success my-4">
        Your password has been reset successfully.
        <br />
        You may now use it to log in.
      </h3>
    )
  }
}
